import {
  type Env,
  getBffApiBaseUrl,
  getEnvFromCurrentHostname,
} from '@ctw/shared/utils/environments';
import { useEffect, useRef } from 'react';

interface UseTriggerHotloadPatientOptions {
  authToken: string | undefined;
  patientUPID?: string;
  patientResourceID?: string;
  patientID?: string;
  systemURL?: string;
  fallbackEnv?: Env;
  enabled?: boolean;
}

/**
 * Triggers hotloading for a patient via the BFF API.
 */
export const useTriggerHotloadPatient = ({
  authToken,
  patientUPID,
  patientResourceID,
  patientID,
  systemURL,
  fallbackEnv,
  enabled = true,
}: UseTriggerHotloadPatientOptions): void => {
  const firedForKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!(enabled && authToken)) {
      return;
    }

    const hasIdentifier =
      Boolean(patientUPID) || Boolean(patientResourceID) || Boolean(patientID && systemURL);

    if (!hasIdentifier) {
      return;
    }

    const body: Record<string, string> = {};
    if (patientUPID) {
      body.patientUPID = patientUPID;
    } else if (patientResourceID) {
      body.patientResourceID = patientResourceID;
    } else if (patientID && systemURL) {
      body.patientID = patientID;
      body.systemURL = systemURL;
    }

    // Guard against React StrictMode's double-invocation and re-renders firing
    // the trigger more than once for the same patient. A genuinely new
    // identifier (a different patient) intentionally clears this guard and fires
    // again.
    const identifierKey = JSON.stringify(body);
    if (firedForKey.current === identifierKey) {
      return;
    }

    let env: Env;
    try {
      env = getEnvFromCurrentHostname({ fallbackEnv });
    } catch {
      // Could not determine the environment — skip the trigger this render.
      // Intentionally leave `firedForKey` unset so a later render (e.g. once
      // `fallbackEnv` resolves) can retry instead of being permanently deduped.
      return;
    }

    // Only mark this identifier as fired once we know the request can actually
    // be sent — i.e. after env resolution succeeds.
    firedForKey.current = identifierKey;

    void fetch(`${getBffApiBaseUrl(env)}/v1/patient/hotload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).catch(() => {
      // Best-effort cache warming; swallow failures.
    });
  }, [authToken, patientUPID, patientResourceID, patientID, systemURL, fallbackEnv, enabled]);
};
